/**
 * Video Sync Panel
 * Import video and synchronize audio tracks to video timeline
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Video,
  Upload,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Maximize2,
  Volume2,
  VolumeX,
  X,
  Film,
  Clock,
  Link,
  Unlink,
  ChevronDown,
  Loader2,
  Trash2,
  Music,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format time as MM:SS:FF (frames)
const formatTimecode = (seconds, fps = 30) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const frames = Math.floor((seconds % 1) * fps);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}:${frames.toString().padStart(2, '0')}`;
};

export const VideoSyncPanel = ({
  isOpen,
  onClose,
  projectId = 'default-project',
  currentTime = 0,
  isPlaying = false,
  onTimeUpdate,
  onPlayStateChange,
  bpm = 120,
}) => {
  // State
  const [video, setVideo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [linkedToDAW, setLinkedToDAW] = useState(true);
  const [startOffset, setStartOffset] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Sync video with DAW timeline
  useEffect(() => {
    if (linkedToDAW && videoRef.current && video) {
      const targetTime = currentTime - startOffset;
      if (targetTime >= 0 && targetTime <= videoDuration) {
        // Only seek if difference is significant (avoid constant seeking)
        if (Math.abs(videoRef.current.currentTime - targetTime) > 0.1) {
          videoRef.current.currentTime = targetTime;
        }
      }
    }
  }, [currentTime, linkedToDAW, startOffset, video, videoDuration]);

  // Sync play state
  useEffect(() => {
    if (linkedToDAW && videoRef.current && video) {
      if (isPlaying && !videoPlaying) {
        videoRef.current.play().catch(() => {});
        setVideoPlaying(true);
      } else if (!isPlaying && videoPlaying) {
        videoRef.current.pause();
        setVideoPlaying(false);
      }
    }
  }, [isPlaying, linkedToDAW, video, videoPlaying]);

  // Handle file upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select MP4, MOV, or WebM file');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);
      
      // Simulate progress (in production, use XMLHttpRequest for real progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);
      
      const response = await fetch(`${API_URL}/api/video/upload`, {
        method: 'POST',
        body: formData,
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const data = await response.json();
      
      // Create object URL for local preview
      const videoUrl = URL.createObjectURL(file);
      
      setVideo({
        ...data.metadata,
        localUrl: videoUrl,
        file: file,
      });
      
      toast.success('Video uploaded successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload video');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Video event handlers
  const handleVideoTimeUpdate = () => {
    if (videoRef.current) {
      setVideoCurrentTime(videoRef.current.currentTime);
      
      // If linked, update DAW timeline
      if (linkedToDAW && !isPlaying) {
        onTimeUpdate?.(videoRef.current.currentTime + startOffset);
      }
    }
  };

  const handleVideoLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  // Playback controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
        setVideoPlaying(false);
        if (linkedToDAW) onPlayStateChange?.(false);
      } else {
        videoRef.current.play();
        setVideoPlaying(true);
        if (linkedToDAW) onPlayStateChange?.(true);
      }
    }
  };

  const seek = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setVideoCurrentTime(time);
      if (linkedToDAW) {
        onTimeUpdate?.(time + startOffset);
      }
    }
  };

  const skipBackward = () => seek(Math.max(0, videoCurrentTime - 5));
  const skipForward = () => seek(Math.min(videoDuration, videoCurrentTime + 5));
  const goToStart = () => seek(0);

  // Extract audio from video
  const extractAudio = async () => {
    if (!video) return;
    
    try {
      const response = await fetch(`${API_URL}/api/video/extract-audio/${video.video_id}`, {
        method: 'POST',
      });
      
      if (!response.ok) throw new Error('Extraction failed');
      
      const data = await response.json();
      toast.success('Audio extracted! Track will be added.');
    } catch (err) {
      toast.error('Failed to extract audio');
    }
  };

  // Remove video
  const removeVideo = () => {
    if (video?.localUrl) {
      URL.revokeObjectURL(video.localUrl);
    }
    setVideo(null);
    setVideoCurrentTime(0);
    setVideoDuration(0);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 300 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 300 }}
        className="fixed right-0 top-0 bottom-0 w-96 bg-zinc-900 border-l border-zinc-800 z-40 flex flex-col shadow-2xl"
        data-testid="video-sync-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Film className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-white">Video Sync</h2>
              <p className="text-xs text-zinc-400">Sync to timeline</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!video ? (
            /* Upload Area */
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isUploading
                  ? 'border-cyan-500/50 bg-cyan-500/10'
                  : 'border-zinc-700 hover:border-zinc-500 bg-zinc-800/50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {isUploading ? (
                <div className="space-y-3">
                  <Loader2 className="w-10 h-10 text-cyan-500 mx-auto animate-spin" />
                  <p className="text-sm text-zinc-400">Uploading video...</p>
                  <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-500 transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <Video className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
                  <h3 className="font-medium text-white mb-1">Import Video</h3>
                  <p className="text-sm text-zinc-400 mb-4">
                    Drag & drop or click to browse
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-cyan-500 hover:bg-cyan-600"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select Video
                  </Button>
                  <p className="text-xs text-zinc-500 mt-3">
                    Supports: MP4, MOV, WebM
                  </p>
                </>
              )}
            </div>
          ) : (
            /* Video Player */
            <>
              {/* Video Preview */}
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <video
                  ref={videoRef}
                  src={video.localUrl}
                  className="w-full h-full object-contain"
                  muted={videoMuted}
                  onTimeUpdate={handleVideoTimeUpdate}
                  onLoadedMetadata={handleVideoLoadedMetadata}
                  onClick={togglePlay}
                />
                
                {/* Video Overlay */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/30">
                  <Button
                    variant="ghost"
                    size="lg"
                    onClick={togglePlay}
                    className="w-16 h-16 rounded-full bg-white/20 hover:bg-white/30"
                  >
                    {videoPlaying ? (
                      <Pause className="w-8 h-8 text-white" />
                    ) : (
                      <Play className="w-8 h-8 text-white ml-1" />
                    )}
                  </Button>
                </div>
                
                {/* Timecode */}
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs font-mono text-white">
                  {formatTimecode(videoCurrentTime)}
                </div>
              </div>
              
              {/* Timeline Scrubber */}
              <div className="space-y-1">
                <input
                  type="range"
                  min="0"
                  max={videoDuration || 100}
                  value={videoCurrentTime}
                  onChange={(e) => seek(parseFloat(e.target.value))}
                  className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                />
                <div className="flex justify-between text-xs text-zinc-500">
                  <span>{formatTimecode(videoCurrentTime)}</span>
                  <span>{formatTimecode(videoDuration)}</span>
                </div>
              </div>
              
              {/* Transport Controls */}
              <div className="flex items-center justify-center gap-2">
                <Button variant="ghost" size="sm" onClick={goToStart} className="text-zinc-400">
                  <SkipBack className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={skipBackward} className="text-zinc-400">
                  -5s
                </Button>
                <Button
                  onClick={togglePlay}
                  className="w-12 h-12 rounded-full bg-cyan-500 hover:bg-cyan-600"
                >
                  {videoPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={skipForward} className="text-zinc-400">
                  +5s
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setVideoMuted(!videoMuted)}
                  className="text-zinc-400"
                >
                  {videoMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Sync Settings */}
              <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-zinc-300">Sync Mode</span>
                  <Button
                    variant={linkedToDAW ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLinkedToDAW(!linkedToDAW)}
                    className={linkedToDAW ? "bg-cyan-500 hover:bg-cyan-600" : ""}
                  >
                    {linkedToDAW ? (
                      <><Link className="w-3 h-3 mr-1" /> Linked</>
                    ) : (
                      <><Unlink className="w-3 h-3 mr-1" /> Unlinked</>
                    )}
                  </Button>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-zinc-300">Start Offset</span>
                    <span className="text-xs text-zinc-500">{startOffset.toFixed(2)}s</span>
                  </div>
                  <input
                    type="range"
                    min="-10"
                    max="10"
                    step="0.1"
                    value={startOffset}
                    onChange={(e) => setStartOffset(parseFloat(e.target.value))}
                    className="w-full h-1 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                  />
                </div>
              </div>
              
              {/* Video Info */}
              <div className="p-3 bg-zinc-800/30 rounded-lg text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Film className="w-4 h-4 text-cyan-400" />
                  <span className="text-white font-medium truncate">{video.filename}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs text-zinc-400">
                  <div>Duration: {formatTimecode(videoDuration)}</div>
                  <div>FPS: {video.fps || 30}</div>
                  <div>Resolution: {video.width}x{video.height}</div>
                  <div>Codec: {video.codec}</div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={extractAudio}
                  className="flex-1 border-zinc-700"
                >
                  <Music className="w-4 h-4 mr-1" />
                  Extract Audio
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={removeVideo}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VideoSyncPanel;
